import type { ReactElement, ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import MarketingStationLayout from 'app/shared-components/MarketingStationLayout';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { StepperBreadcrumb } from './StepperBreadcrumb';

interface OnboardingLayoutProps {
  children: ReactNode;
  backTo?: string;
  primaryLabel?: string;
  onPrimary?: () => void;
  primaryTo?: string;
  primaryDisabled?: boolean;
  transparentBg?: boolean;
  activeStep?: number;
}

export function OnboardingLayout({
  children,
  backTo,
  primaryLabel,
  onPrimary,
  primaryTo,
  primaryDisabled,
  transparentBg = false,
  activeStep,
}: OnboardingLayoutProps): ReactElement {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleBack = () => {
    if (backTo) {
      navigate({ pathname: backTo, search: searchParams.toString() });
    }
  };

  const handlePrimary = () => {
    if (onPrimary) {
      onPrimary();
    } else if (primaryTo) {
      navigate({ pathname: primaryTo, search: searchParams.toString() });
    }
  };

  return (
    <MarketingStationLayout className="vtwo:grow vtwo:flex vtwo:flex-col vtwo:overflow-y-auto">
      <div className="vtwo:flex vtwo:flex-1 vtwo:p-6">
        <div
          className={cn(
            'vtwo:flex-1 vtwo:flex vtwo:flex-col vtwo:max-w-6xl vtwo:w-full vtwo:mx-auto vtwo:px-10 vtwo:py-10 vtwo:rounded-xl',
            !transparentBg && 'vtwo:bg-white',
            activeStep !== undefined && 'vtwo:pt-5'
          )}
        >
          {activeStep !== undefined && (
            <div className="vtwo:mb-8">
              <StepperBreadcrumb activeStep={activeStep} />
            </div>
          )}
          <div className="vtwo:flex-1">{children}</div>

          {(backTo || primaryLabel) && (
            <div className="vtwo:flex vtwo:items-center vtwo:justify-between vtwo:pt-10">
              {backTo ? (
                <Button
                  variant="ghost"
                  size="xl"
                  onClick={handleBack}
                  className="vtwo:rounded-full"
                >
                  <ArrowLeft className="vtwo:size-5" />
                  Back
                </Button>
              ) : (
                <div />
              )}
              {primaryLabel && (
                <Button
                  onClick={handlePrimary}
                  disabled={primaryDisabled}
                  size="xl"
                  className="vtwo:bg-featured vtwo:min-w-[240px] vtwo:hover:bg-featured-hover vtwo:text-white vtwo:font-semibold vtwo:text-base vtwo:px-12 vtwo:py-3 vtwo:rounded-full"
                >
                  {primaryLabel}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </MarketingStationLayout>
  );
}
